import ViktorClient from '@/api/clients/viktorClient/ViktorClient';
/* CONSTANTS */
import { DEFAULT_PER_PAGE } from '@/api/constants';
import type { PaginatedResponse } from '@/types/api/axios';
/* TYPES */
import type {
  Me,
  MeGroupsResponse,
  MeNotificationsResponse,
  MeSharepool,
  MeSharepoolsResponse
} from '@/types/api/me/me';
import type { RoleUser } from '@/types/enums/enums.types';
import type { NotificationStatus } from '@/types/notifications/notification';

/** GET **/

/**
 * Get data about current user
 *
 * @return {Promise<Auth>} Promise with data about the current connected user
 */
export function getMe(): Promise<Me> {
  return ViktorClient().get(`/me`);
}

/**
 * Get current recruiter's invitation link
 * @param {string} sharepoolUuid - sharepool uuid
 * @return {Promise<{ url: string }>} Promise with invitation link url
 */
export function getMeInvitationLink({ sharepoolUuid }: { sharepoolUuid: string }): Promise<{ url: string }> {
  const params = {
    short: true,
    sharepool_uuid: sharepoolUuid
  };
  return ViktorClient().get('/me/invitation/sharepool/link', { params });
}

/**
 *Get accepted sharepools for the current user
 *
 * @param params
 * @param {String} params.searchTerm - searched term
 * @param {String} params.role - Role inside sharepool
 * @param {Number} params.page - page to fetch
 *
 * @return {Promise<MeSharepoolsResponse>} Promise with data about sharepools and meta
 */
export function getMeSharepools({
  searchTerm,
  role = 'recruiter',
  page = 1
}: {
  searchTerm: string;
  page: number;
  role?: RoleUser;
}): Promise<MeSharepoolsResponse> {
  // Init API Call
  const params = {
    page,
    per_page: DEFAULT_PER_PAGE,
    status_eid: 'accepted',
    role_eid: role,
    fields: 'sharepool.branding',
    'search[sharepool:label]': searchTerm?.trim() || undefined
  };

  return ViktorClient().get(`/me/sharepools`, { params });
}

/**
 * Get the recruiter's sharepool with branding
 */
export function getMeSharepoolsBranding(): Promise<PaginatedResponse<MeSharepool[]>> {
  const params = {
    fields: 'sharepool.branding'
  };
  return ViktorClient().get(`/me/sharepools`, { params });
}

/**
 * Get available groups for the connected recruiter
 * @param data
 * @param {string} data.searchTerm - search term
 * @param {number} data.page - page to fetch
 * @param {boolean} data.withCandidates - should we also fetch the group members
 * @param {boolean} data.withCreator - should we fetch creator fields
 * @param {Array<string>} data.count_candidates - array of assessments to count candidates
 */
export function getMeGroups({
  searchTerm,
  page = 1,
  withCandidates = false,
  withCreator = true,
  createdBy = '',
  sharepoolUuids = [] as string[],
  count_candidates = [] as string[]
}): Promise<MeGroupsResponse> {
  const params = {
    order: 'label:asc',
    page,
    per_page: DEFAULT_PER_PAGE,
    'search[label]': searchTerm?.trim() || undefined,
    fields: withCreator
      ? 'creator:first_name,creator:last_name,creator:uuid,creator:email,creator:branding,sharepool_uuid,creator:disc_id'
      : undefined,
    with_candidates: withCandidates || undefined,
    created_by: createdBy || undefined,
    sharepool_uuid: sharepoolUuids.length ? sharepoolUuids : undefined,
    count_candidates: count_candidates.length ? count_candidates : undefined
  };
  return ViktorClient().get(`/me/groups`, { params });
}

/**
 * Request to get current user notifications
 * @param {number} page The page requested
 * @param {number} perPage The total number of pages
 * @param {string[]} fields Fields requested from call
 * @returns The promise call with all user notifications
 */
export function getMeNotifications({
  page,
  perPage = DEFAULT_PER_PAGE,
  fields = ['created_at']
}): Promise<MeNotificationsResponse> {
  const params = {
    page,
    per_page: perPage,
    order: 'created_at:desc',
    fields
  };

  return ViktorClient().get('/me/notifications', { params });
}

/** PUT CALLS **/
/**
 * Request to update notifications
 * @returns {Promise<void>} promise for success or failure
 */
export function putMeNotifications(uuids: string[], status: NotificationStatus): Promise<void> {
  const notifications = uuids.map(uuid => ({ uuid, status }));
  return ViktorClient().put('/me/notifications', {
    notifications
  });
}

/**
 * Allows to mark all notifs of a user as a given status
 * @param {NotificationStatus} status status to update to
 * @returns {Promise<void>} promise for success or failure
 */
export const putAllNotifications = (status: NotificationStatus = 'read'): Promise<void> => {
  const params = {
    status
  };
  return ViktorClient().put('/me/notifications/user', params);
};

/** DELETE CALLS **/
/**
 * Request to delete notifications
 * @param {Array<string>} notifications array of notification uuids to delete
 * @returns {Promise<void>} promise for success or failure
 */
export function deleteMeNotifications(notificationUuids: string[]): Promise<void> {
  const notifications = notificationUuids.map(uuid => ({ uuid }));
  return ViktorClient().delete('/me/notifications', { data: { notifications } });
}
